import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseText from '../BaseText';
import { useDispatch } from 'react-redux';
import { i18nNS, useComponentTKey } from '../../i18n';
import { forgotPassword, checkAnswer, getNextQuestion, logout } from '../../store/actions/auth';
import { EXPENSEPOINT_ENABLED, REBOOK_ENABLED, CREW_ENABLED } from '../../config/env';
import { CommonProperties, CommonStyles, Color } from '../../styles';
import { View, Image, StyleSheet } from 'react-native';
import InputForm from '../InputForm';
import { isNullOrEmpty } from '../../utils/common.utils';
import { openAlert } from '../../store/actions/alert';
import Button, { BUTTON_TYPES } from '../Button';

const SUCCESS = 'Success';
const RIGHT_ANSWER = 'rightAnswer';
const WRONG_ANSWER = 'wrongAnswer';
const CONTACT_ADMIN_ERROR = 'Please contact your system administrator';
const GENERIC_ERROR = 'Error';
const UNSUCCESSFUL = 'Unsuccessful';
const MAX_QUESTION_COUNT = 3;
const EMAIL_REGEX = /^([a-zA-Z0-9_.!-+])+@(([a-zA-Z0-9-])+.)+([a-zA-Z0-9]{2,4})+$/;
const PENDING_USER = 'Pending User';

const ForgotPassword = ({ hideModal }) => {
  const { t } = useTranslation(i18nNS.login);
  const { tKey } = useComponentTKey('ForgotPasswordScreen');

  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [answer, setAnswer] = useState('');
  const [errorText, setErrorText] = useState('');
  const [showAnswerTextField, setShowAnswerTextField] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [questionId, setQuestionId] = useState();
  const [passwordResetSuccessPopup, setPasswordResetSuccessPopup] = useState(false);
  const [passwordResetFailurePopUp, setPasswordResetFailurePopUp] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState('');
  const [nextQuestionData, setNextQuestionData] = useState('');

  const [nextQuestionLoading, setNextQuestionLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [checkAnswerLoading, setCheckAnswerLoading] = useState(false);

  const validateEmail = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorText(t(tKey('EmailErrorMessage')));
      return false;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setErrorText(t(tKey('InvalidEmailErrorMessage')));
      return false;
    }

    setErrorText('');
    return true;
  };

  const handleCheckAnswerResponse = (response) => {
    if (response === RIGHT_ANSWER) {
      setPasswordResetSuccessPopup(true);
    } else if (response === WRONG_ANSWER) {
      handleWrongAnswer();
    }
  };

  const handleCheckAnswer = async () => {
    setCheckAnswerLoading(true);
    try {
      const response = await dispatch(checkAnswer(email, answer, questionId, 1));
      handleCheckAnswerResponse(response);
    } catch (error) {
      console.error('Error while checking answer:', error);
    } finally {
      setCheckAnswerLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (validateEmail()) {
      setForgotPasswordLoading(true);
      try {
        const res = await dispatch(forgotPassword(email));
        setForgotPasswordStatus(res);
      } catch (error) {
        console.error('Error while checking answer:', error);
      } finally {
        setForgotPasswordLoading(false);
      }
    }
  };

  const handleSubmit = () => {
    showAnswerTextField ? handleCheckAnswer() : handleForgotPassword();
  };

  const handleClose = () => {
    dispatch(logout());
  };

  const handleWrongAnswer = () => {
    let newCount = questionCount + 1;

    if (newCount < MAX_QUESTION_COUNT) {
      handleNextQuestion();
      setQuestionCount(newCount);
      setShowAnswerTextField(true);
      setErrorText(t(tKey('InvalidAnswerErrorMessage')));
    } else {
      setPasswordResetSuccessPopup(false);
      setPasswordResetFailurePopUp(true);
    }
  };

  const handleNextQuestion = async () => {
    setNextQuestionLoading(true);
    try {
      const nextQues = await dispatch(getNextQuestion(email));
      setNextQuestionData(nextQues);
      setAnswer('');
      setErrorText('');
    } catch (error) {
      console.error('Error while checking answer:', error);
    } finally {
      setNextQuestionLoading(false);
    }
  };

  useEffect(() => {
    if (!isNullOrEmpty(forgotPasswordStatus)) {
      switch (forgotPasswordStatus) {
        case PENDING_USER:
          setErrorText(t(tKey('InactiveUserError')));
          break;
        case UNSUCCESSFUL:
          setPasswordResetSuccessPopup(true);
          break;
        case GENERIC_ERROR:
          setErrorText(t(tKey('EmailFailureError')));
          break;
        case CONTACT_ADMIN_ERROR:
          setErrorText(t(tKey('ContactAdminError')));
          break;
        default:
          break;
      }
      if (forgotPasswordStatus === SUCCESS) {
        handleNextQuestion();
      }
    }
  }, [forgotPasswordStatus]);

  useEffect(() => {
    if (
      nextQuestionData &&
      ![CONTACT_ADMIN_ERROR, GENERIC_ERROR, UNSUCCESSFUL, ''].includes(nextQuestionData)
    ) {
      const [id, text] = nextQuestionData.split(',');
      setQuestionId(id);
      setQuestionText(text);
      setShowAnswerTextField(true);
    }
  }, [nextQuestionData]);

  const returnToLogin = () => {
    hideModal();
  };

  const renderPasswordResetSuccess = () => {
    dispatch(
      openAlert({
        title: t(tKey('SuccessMessage')),
        confirmText: t(tKey('BackButton')),
        onConfirmPressed: () => {
          returnToLogin();
        },
      })
    );
  };

  const renderPasswordResetFailure = () => {
    dispatch(
      openAlert({
        title: t(tKey('ContactAdminMessage')),
        confirmText: t(tKey('BackButton')),
        onConfirmPressed: () => dispatch(logout()),
      })
    );
  };

  const renderSecurityQuestion = () => {
    return (
      <>
        <BaseText style={styles.inputText}>{t(tKey('AnswerSecurityQuestion'))}</BaseText>
        <BaseText style={styles.inputText}>{questionText}</BaseText>
      </>
    );
  };

  const renderEmailInput = () => {
    return (
      <InputForm
        placeholder={t(tKey('Email'))}
        value={email}
        onChangeText={(e) => setEmail(e.trim())}
        required={true}
        type="email"
        errorMessage={errorText}
      />
    );
  };

  const renderAnswerInput = () => {
    return (
      <InputForm
        value={answer}
        onChangeText={(e) => setAnswer(e)}
        required={true}
        errorMessage={errorText}
        secure={true}
      />
    );
  };

  return (
    <View style={[styles.body, REBOOK_ENABLED ? { marginTop: 0, marginBottom: 0 } : null]}>
      <>
        <Image
          style={CREW_ENABLED ? styles.crewLogo : styles.logo}
          source={
            EXPENSEPOINT_ENABLED
              ? require('../../assets/img/logo-expensepoint.png')
              : CREW_ENABLED
              ? require('../../assets/img/logo-crew-travel.png')
              : require('../../assets/img/logo.png')
          }
        />
        <View style={styles.form}>
          <BaseText style={styles.forgotPswdText}>{t(tKey('ForgotPasswordText'))}</BaseText>

          {passwordResetSuccessPopup && renderPasswordResetSuccess()}
          {passwordResetFailurePopUp && renderPasswordResetFailure()}
          {!passwordResetSuccessPopup && !passwordResetFailurePopUp && (
            <>
              {showAnswerTextField ? (
                renderSecurityQuestion()
              ) : (
                <BaseText style={styles.inputText}>{t(tKey('EnterEmail'))}</BaseText>
              )}

              {!showAnswerTextField && renderEmailInput()}
              {showAnswerTextField && renderAnswerInput()}

              {checkAnswerLoading || forgotPasswordLoading || nextQuestionLoading ? (
                <Button
                  label={t(tKey('PleaseWait'))}
                  style={styles.disabledButton}
                  labelStyle={CommonStyles.h2}
                  onPress={handleSubmit}
                  disabled={true}
                />
              ) : (
                <Button
                  label={t(tKey('SubmitButton'))}
                  style={styles.submitButton}
                  labelStyle={CommonStyles.h2}
                  onPress={handleSubmit}
                />
              )}
              <Button
                label={t(tKey('BackButton'))}
                labelStyle={CommonStyles.h2}
                onPress={handleClose}
                type={BUTTON_TYPES.TEXT}
              />
            </>
          )}
        </View>
      </>
    </View>
  );
};

const styles = StyleSheet.create({
  logo: {
    resizeMode: 'cover',
    width: '90%',
    height: 60,
    maxWidth: 300,
    ...CommonProperties.mb5,
  },
  crewLogo: {
    resizeMode: 'cover',
    width: '90%',
    height: 100,
    maxWidth: 220,
    ...CommonProperties.mb5,
  },
  body: {
    marginTop: '20%',
    marginBottom: 60,
    alignContent: 'stretch',
    alignItems: 'center',
    ...CommonProperties.p5,
  },
  forgotPswdText: {
    textAlign: 'left',
    ...CommonProperties.h2,
    marginBottom: 10,
  },
  inputText: {
    textAlign: 'left',
    ...CommonProperties.h3,
    marginBottom: 10,
  },
  form: {
    width: '100%',
    maxWidth: 420,
  },
  submitButton: {
    ...CommonProperties.borderRadius4,
    ...CommonProperties.py3,
    ...CommonProperties.boxShadow,
  },
  disabledButton: {
    backgroundColor: Color.disabled,
    ...CommonProperties.borderRadius4,
    ...CommonProperties.py3,
    ...CommonProperties.boxShadow,
  },
});
export default ForgotPassword;
